import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { type ForwardedRef, forwardRef, type FunctionComponent, memo, type ReactNode } from 'react';
import { NavSegmentButton } from './NavSegmentButton';
export interface NavSegmentOption<T> {
  value: T;
  label: string;
  /** Minimum label width (e.g. `'10.84em'`), so the tab keeps its size when a web font swaps in. */
  labelWidth?: string;
  href?: string;
  external?: boolean;
  prefetch?: boolean;
  isDisabled?: boolean;
  tooltip?: ReactNode;
  counter?: number;
}
type NavSegmentControlVariant = 'secondary' | 'ghost';
type NavSegmentControlSize = 'default' | 'medium';
type NavSegmentControlOrientation = 'horizontal' | 'vertical';
export interface NavSegmentControlProps<T> {
  id: string;
  options: readonly NavSegmentOption<T>[];
  value: string;
  variant?: NavSegmentControlVariant;
  noPadding?: boolean;
  size?: NavSegmentControlSize;
  className?: string;
  onChange?: (value: T) => void;
  fullWidth?: boolean;
  /** `vertical` stacks options one per row — for surfaces where a horizontal row would overflow. */
  orientation?: NavSegmentControlOrientation;
}
function NavSegmentControlInner(t0, ref) {
  const $ = _c(42);
  let className;
  let id;
  let onChange;
  let options;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    ({
      options,
      id,
      value,
      noPadding: t1,
      variant: t2,
      size: t3,
      className,
      onChange,
      fullWidth: t4,
      orientation: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = id;
    $[3] = onChange;
    $[4] = options;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = value;
  } else {
    className = $[1];
    id = $[2];
    onChange = $[3];
    options = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    value = $[11];
  }
  const noPadding = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "secondary" : t2;
  const size = t3 === undefined ? "default" : t3;
  const fullWidth = t4 === undefined ? false : t4;
  const orientation = t5 === undefined ? "horizontal" : t5;
  const isVertical = orientation === "vertical";
  let t6;
  let t7;
  if ($[12] !== className || $[13] !== fullWidth || $[14] !== isVertical || $[15] !== noPadding || $[16] !== ref || $[17] !== variant) {
    const baseClassName = cn("flex h-fit rounded-lg", isVertical && "flex-col", variant === "ghost" ? "bg-transparent" : "bg-muted", "border-none", noPadding ? "p-0" : "p-[3px]", "gap-2xs", fullWidth || isVertical ? "w-full" : "w-fit");
    t6 = ref;
    t7 = cn(baseClassName, className);
    $[12] = className;
    $[13] = fullWidth;
    $[14] = isVertical;
    $[15] = noPadding;
    $[16] = ref;
    $[17] = variant;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  let t8;
  if ($[20] !== fullWidth || $[21] !== id || $[22] !== isVertical || $[23] !== noPadding || $[24] !== onChange || $[25] !== options || $[26] !== size || $[27] !== value) {
    let t9;
    if ($[29] !== fullWidth || $[30] !== id || $[31] !== isVertical || $[32] !== noPadding || $[33] !== onChange || $[34] !== size || $[35] !== value) {
      t9 = option => <Tooltip key={id + option.value}><TooltipTrigger asChild={true}><div className={cn(isVertical ? "w-full" : fullWidth && "min-w-0 flex-1")}><NavSegmentButton option={option} value={value} size={size} fullWidth={fullWidth || isVertical} vertical={isVertical} onChange={onChange} /></div></TooltipTrigger>{!!option.tooltip && <TooltipContent sideOffset={noPadding ? 4 : 9}>{option.tooltip}</TooltipContent>}</Tooltip>;
      $[29] = fullWidth;
      $[30] = id;
      $[31] = isVertical;
      $[32] = noPadding;
      $[33] = onChange;
      $[34] = size;
      $[35] = value;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    t8 = options.map(t9);
    $[20] = fullWidth;
    $[21] = id;
    $[22] = isVertical;
    $[23] = noPadding;
    $[24] = onChange;
    $[25] = options;
    $[26] = size;
    $[27] = value;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[37] !== props || $[38] !== t6 || $[39] !== t7 || $[40] !== t8) {
    t9 = <div ref={t6} className={t7} {...props}>{t8}</div>;
    $[37] = props;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
}
type TypedMemo<C extends FunctionComponent<never>> = C & {
  displayName?: string;
};

// Create a generic version of forwardRef
// oxlint-disable-next-line typescript/consistent-type-assertions
const NavSegmentControl = forwardRef(NavSegmentControlInner) as <T>(props: NavSegmentControlProps<T> & {
  ref?: ForwardedRef<HTMLDivElement>;
}) => ReturnType<typeof NavSegmentControlInner>;

// oxlint-disable-next-line typescript/consistent-type-assertions
export default memo(NavSegmentControl) as TypedMemo<typeof NavSegmentControl>;